import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import axios from '../../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../../utils/ApiUtils';
import PageHeader from '../../../../layouts/PageHeader';
import { FileMinus } from 'lucide-react';
import CreditNoteForm from './CreditNoteFormBootstrap';
import InvoicePreviewBootstrap from './InvoicePreviewBootstrap';
import InvoiceActionsBootstrap from './InvoiceActionsBootstrap';
import isEqual from 'lodash/isEqual';
import { v4 as uuidv4 } from 'uuid';
import { useInvoiceSettings } from '../../../../../context/InvoiceSettingsContext';
import TemplateSelectorBootstrap from './TemplateSelectorBootstrap';


const CreditNoteGeneratorBootstrap = () => {
    const history = useHistory();
    const location = useLocation();
    const { companyId, invoiceId } = useParams();
    const invoiceRef = useRef(null);
    const { invoiceSettings } = useInvoiceSettings();

    // Query parameter handling for direct access from invoice list
    const queryParams = new URLSearchParams(location.search);
    const originalInvoiceId = queryParams.get('invoice') || invoiceId;

    // Common state
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState("editor");
    const [isFormValid, setIsFormValid] = useState(false);


    // Invoice data state
    const [originalInvoice, setOriginalInvoice] = useState(null);
    const [creditNote, setCreditNote] = useState(null);
    const [creditNoteId, setCreditNoteId] = useState(null);

    // Form state
    const [initialFormData, setInitialFormData] = useState(null);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState("classic");

    // Delivery state
    const [deliveryMethod, setDeliveryMethod] = useState('download');
    const [deliveryDetails, setDeliveryDetails] = useState({
        email: '',
        phone: '',
        message: 'Här är din kreditfaktura enligt överenskommelse.'
    });

    // Credit note form data
    const [formData, setFormData] = useState({
        invoiceNumber: 'NY KREDITFAKTURA',
        invoiceDate: new Date(),
        dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
        customer: null,
        items: [],
        notes: "",
        paymentInfo: '',
        paymentMethods: {
            bankgiro: true,
            creditCard: false,
            cash: false
        },
        roundTotal: false,
        originalInvoiceId: null,
        footerData: {}
    });

    // Initialize credit note data
    useEffect(() => {
        const initialize = async () => {
            if (!originalInvoiceId) return;

            setLoading(true);
            setError(null);

            try {
                // Fetch the original invoice
                const invoiceResponse = await axios.get(
                    getApiUrl(`${companyId}/invoices/${originalInvoiceId}/details`),
                    { withCredentials: true }
                );

                const invoiceData = invoiceResponse.data.data;
                setOriginalInvoice(invoiceData);

                // Determine if we're in edit or create mode
                const isEditMode = invoiceData.invoice_type === 'credit_note';

                if (isEditMode) {
                    await initializeEditMode(invoiceData);
                } else {
                    initializeCreateMode(invoiceData);
                }
            } catch (error) {
                console.error('Error initializing credit note form:', error);
                setError('Kunde inte läsa in fakturainformation');
            } finally {
                setLoading(false);
            }
        };

        initialize();
    }, [companyId, originalInvoiceId]);

    // Helper function to initialize edit mode
    const initializeEditMode = async (invoiceData) => {
        // Set credit note data
        setCreditNote(invoiceData);
        setCreditNoteId(originalInvoiceId);

        let originalInvoiceData = null;
        let originalInvoiceNumber = '';

        // If this credit note has a related invoice, fetch it
        if (invoiceData.related_invoice_id) {
            try {
                const response = await axios.get(
                    getApiUrl(`${companyId}/invoices/${invoiceData.related_invoice_id}/details`),
                    { withCredentials: true }
                );
                originalInvoiceData = response.data.data;
                setOriginalInvoice(originalInvoiceData);
                originalInvoiceNumber = originalInvoiceData.invoicenumber;
            } catch (error) {
                console.error('Failed to fetch original invoice:', error);
                // Continue without original invoice data
            }
        }

        // Initialize with existing credit note data
        const newFormData = {
            invoiceNumber: invoiceData.invoicenumber,
            invoiceDate: parseISO(invoiceData.invoicedate),
            dueDate: parseISO(invoiceData.deadline),
            customer: mapCustomerData(invoiceData),
            items: mapItemsData(invoiceData.items),
            notes: invoiceData.notes || `Kreditfaktura för faktura #${originalInvoiceNumber}`,
            paymentInfo: invoiceData.payment_info || '',
            paymentMethods: {
                bankTransfer: true,
                creditCard: invoiceData.payment_methods?.includes('creditcard'),
                cash: invoiceData.payment_methods?.includes('cash')
            },
            roundTotal: !invoiceData.rounding,
            originalInvoiceId: invoiceData.related_invoice_id,
            footerData: invoiceData.footer_data || {}
        };

        setFormData(newFormData);
        setInitialFormData(JSON.parse(JSON.stringify(newFormData)));

        // Pre-fill delivery details
        if (invoiceData.customeremail) {
            updateDeliveryEmail(invoiceData.customeremail);
        }
    };

    // Helper function to initialize create mode
    const initializeCreateMode = (invoiceData) => {
        // Convert invoice items to credit note items with zero quantity
        const mappedItems = mapItemsData(invoiceData.items, true);

        const newFormData = {
            invoiceNumber: 'NY KREDITFAKTURA',
            invoiceDate: new Date(),
            dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
            customer: mapCustomerData(invoiceData),
            items: mappedItems,
            notes: `Kreditfaktura för faktura #${invoiceData.invoicenumber}`,
            paymentInfo: '',
            paymentMethods: {
                bankTransfer: true,
                creditCard: false,
                cash: false
            },
            roundTotal: !invoiceData.rounding,
            originalInvoiceId: invoiceData.invoiceid || invoiceData.id,
            footerData: invoiceData.footer_data || {}
        };

        setFormData(newFormData);
        setInitialFormData(JSON.parse(JSON.stringify(newFormData)));

        // Pre-fill delivery details
        if (invoiceData.customeremail) {
            updateDeliveryEmail(invoiceData.customeremail);
        }
    };

    // Helper function to map customer data
    const mapCustomerData = (invoiceData) => ({
        id: invoiceData.customerid,
        name: invoiceData.customername,
        orgnumber: invoiceData.customerorgnumber,
        address: invoiceData.customeraddress,
        zipCode: invoiceData.customerzip,
        city: invoiceData.customercity,
        email: invoiceData.customeremail,
        phone: invoiceData.customerphone
    });

    // Helper function to map items data
    const mapItemsData = (items, resetQuantity = false) =>
        items.map(item => ({
            ...item,
            id: item.id || uuidv4(),
            quantity: resetQuantity ? 0 : item.quantity,
            original_quantity: item.original_quantity || item.quantity
        }));

    // Helper function to update delivery email
    const updateDeliveryEmail = (email) => {
        setDeliveryDetails(prev => ({
            ...prev,
            email
        }));
    };

    // Track changes
    useEffect(() => {
        if (initialFormData) {
            // Ignore date objects when comparing since they won't be equal by reference
            const hasChanges = !isEqual(
                { ...formData, invoiceDate: null, dueDate: null },
                { ...initialFormData, invoiceDate: null, dueDate: null }
            );
            setHasUnsavedChanges(hasChanges);
        }
    }, [formData, initialFormData]);

    useEffect(() => {
        // Check if items have been selected for credit
        const hasItemsToCredit = formData.items.some(item =>
            item.type !== 'Text' && item.quantity > 0
        );

        // Check if any quantities exceed original
        const invalidQuantities = formData.items.filter(item =>
            item.type !== 'Text' &&
            item.quantity > item.original_quantity
        );

        // Update validity state without showing toasts
        setIsFormValid(hasItemsToCredit && invalidQuantities.length === 0);
    }, [formData.items]);

    // Handler for form data changes
    const handleFormDataChange = useCallback((newData) => {
        setFormData(current => ({ ...current, ...newData }));
    }, []);

    // Handler for delivery method changes
    const handleDeliveryMethodChange = useCallback((method) => {
        setDeliveryMethod(method);
    }, []);

    // Handler for delivery details changes
    const handleDeliveryDetailsChange = useCallback((details) => {
        setDeliveryDetails(details);
    }, []);

    // Validate form before save/publish
    const validateForm = useCallback(() => {

        // Check if any items are selected for credit
        const hasItemsToCredit = formData.items.some(item =>
            item.type !== 'Text' && item.quantity > 0
        );

        if (!hasItemsToCredit) {
            toast.error('Vänligen välj minst en artikel att kreditera');
            return false;
        }

        // Check if any quantities exceed original
        const invalidQuantities = formData.items.filter(item =>
            item.type !== 'Text' &&
            item.quantity > item.original_quantity
        );

        if (invalidQuantities.length > 0) {
            toast.error('Krediterat antal kan inte överstiga ursprungligt antal');
            return false;
        }

        return true;
    }, [formData.items]);

    // Save draft implementation
    const handleSaveDraft = async () => {
        if (!validateForm()) return null;

        // Implementation would go here...
        return creditNoteId;
    };

    // Publish credit note
    const handlePublish = async () => {
        if (!validateForm()) return;

        // Implementation would go here...
    };

    /*const handleSelectTemplate = (templateId) => {
        if (templateId) {
            setSelectedTemplate(templateId);
            // Animate the preview when template changes
            if (invoiceRef.current) {
                invoiceRef.current.classList.add('scale-in');
                setTimeout(() => {
                    invoiceRef.current?.classList.remove('scale-in');
                }, 300);
            }
        }
    };*/

    const handleSelectTemplate = (templateId) => {
        if (templateId === 'classic') {
            setSelectedTemplate(templateId);
            // Animate the preview when template changes
            if (invoiceRef.current) {
                invoiceRef.current.classList.add('scale-in');
                setTimeout(() => {
                    invoiceRef.current?.classList.remove('scale-in');
                }, 300);
            }
        }
    };


    // Loading indicator
    if (loading && !originalInvoice) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '80vh' }}>
                <div className="spinner-border" role="status">
                    <span className="visually-hidden">Laddar...</span>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="alert alert-danger">
                {error}
                <div className="mt-3">
                    <button
                        className="btn btn-outline-primary"
                        onClick={() => history.push(`/${companyId}/invoices`)}
                    >
                        Tillbaka till fakturalistan
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="d-flex flex-column h-100">
            <PageHeader
                backTo={{ to: `/${companyId}/invoices`, label: 'Tillbaka till fakturalistan' }}
                icon={FileMinus}
                title={creditNoteId ? 'Redigera kreditfaktura' : 'Skapa kreditfaktura'}
            />

            {/* Actions Bar */}
            <div className="mb-4">
                <InvoiceActionsBootstrap
                    invoiceRef={invoiceRef}
                    onSaveDraft={handleSaveDraft}
                    onPublish={handlePublish}
                    isValid={isFormValid}
                    deliveryMethod={deliveryMethod}
                    onDeliveryMethodChange={handleDeliveryMethodChange}
                    deliveryDetails={deliveryDetails}
                    onDeliveryDetailsChange={handleDeliveryDetailsChange}
                    paymentMethods={formData.paymentMethods}
                    onPaymentMethodChange={(methods) => handleFormDataChange({ paymentMethods: methods })}
                    availableDeliveryMethods={invoiceSettings?.deliveryChannels}
                    hasUnsavedChanges={hasUnsavedChanges}
                    invoiceSettings={invoiceSettings}
                    isLoading={loading}
                    isCreditNote={true}
                />
            </div>

            <div className="mb-4">
                <TemplateSelectorBootstrap
                    selectedTemplate={selectedTemplate}
                    onSelectTemplate={handleSelectTemplate}
                    availableTemplates={invoiceSettings?.template}
                />
            </div>

            <div className="section-header mb-3">
                <h5 className="mb-0">Kreditfakturadetaljer</h5>
                <p className="text-muted small mb-0">Välj vilka artiklar som ska krediteras och justera antal för delkrediteringar. Välj fakturadatum och förfallodatum. Fakturanummer kommer genereras automatiskt när fakturan publiceras.</p>
            </div>

            {/* Content Area */}
            <div className="flex-grow-1 h-100">
                {/* Editor Tab */}
                <div className={`tab-content h-100 ${activeTab === 'editor' ? 'd-block' : 'd-none'}`}>
                    <div className="bg-white rounded shadow-sm p-4 h-100 overflow-auto">
                        <CreditNoteForm
                            formData={formData}
                            onFormDataChange={handleFormDataChange}
                            originalInvoice={originalInvoice}
                            isLoading={loading}
                        />
                    </div>
                </div>

            </div>

            {/* Floating Action Bar */}
            {/*hasUnsavedChanges && (
                <div className="position-fixed bottom-0 start-0 end-0 bg-white shadow-lg py-2 px-3"
                    style={{ zIndex: 1030 }}>
                    <div className="container-fluid">
                        <div className="row align-items-center">
                            <div className="col-auto me-auto">
                                <span className="text-warning d-flex align-items-center">
                                    <i className="fa fa-exclamation-triangle me-2"></i>
                                    Osparade ändringar
                                </span>
                            </div>
                            <div className="col-auto">
                                <button
                                    className="btn btn-outline-secondary me-2"
                                    onClick={handleSaveDraft}
                                    disabled={loading}
                                >
                                    <i className="fa fa-save me-1"></i> Spara
                                </button>

                                <button
                                    className="btn btn-primary"
                                    onClick={handlePublish}
                                    disabled={loading || !formData.items.some(item => item.quantity > 0)}
                                >
                                    <i className="fa fa-paper-plane me-1"></i> Publicera
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )*/}

            {/* Spacer for floating bar */}
            <div style={{ height: hasUnsavedChanges ? '60px' : '0' }}></div>
        </div>
    );
};

export default CreditNoteGeneratorBootstrap;